import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Flex,
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import { TagsInput } from "react-tag-input-component";
import { useNavigate, useParams } from "react-router-dom";
import { useMedicationsPutMutation } from "./api/useMedicationsMutation";
import { useMedicationQuery } from "./api/useMedicationsQuery";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { useUserQuery } from "../common/useUserQuery";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";

const date = z.union([
  z.number({ message: "Invalid Format. YYYY-MM-DD Format." }),
  z.string({ message: "Invalid Format. YYYY-MM-DD Format." }),
  z.date({ message: "Invalid Format. YYYY-MM-DD Format." }),
]);
const putMedicationsRequest = z.object({
  medicationName: z
    .string({ message: "Name is Required" })
    .min(1, { message: "Name is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  medicationDescription: z
    .string({ message: "Description is Required" })
    .min(1, { message: "Description is Required" }),
  beginDate: date,
  endDate: z
    .union([z.string().length(0, "Invalid Format. YYYY-MM-DD Format."), date])
    .optional()
    .transform((e) => (e === "" ? null : e))
    .nullish(),
  usage: z
    .string({ message: "Usage is Required" })
    .min(1, { message: "Usage is Required" }),
  dosage: z
    .string({ message: "Dosage is Required" })
    .min(1, { message: "Dosage is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  diagnosisTags: z.array(z.string()).nullish(),
  status: z
    .string({ message: "Status is Required" })
    .min(1, { message: "Status is Required" }),
  medicationType: z
    .string({ message: "Type is Required" })
    .min(1, { message: "Type is Required" }),
  isActive: z.optional(z.number().int()),
});

export default function MedicationsEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { data: userQueryData } = useUserQuery();
  const { mutate: medicationsPutMutation } = useMedicationsPutMutation();
  const { data, isFetching } = useMedicationQuery(
    params.id,
    userQueryData?.patientUserId
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(putMedicationsRequest) });

  useEffect(() => {
    if (data) {
      reset({
        medicationName: data?.medicationName,
        medicationDescription: data?.medicationDescription,
        beginDate: data?.beginDate,
        endDate: data?.endDate,
        usage: data?.usage,
        dosage: data?.dosage,
        diagnosisTags: JSON.parse(data?.diagnosisTags),
        status: data?.status,
        medicationType: data?.medicationType,
        isActive: data?.isActive,
      });
    }
  }, [data]);

  const onSubmit = (values) => {
    const formatBeginDate = new Date(values.beginDate);

    let medicationParams = {
      id: params.id,
      patientUserId: userQueryData?.patientUserId,
      medicationName: values.medicationName,
      medicationDescription: values.medicationDescription,
      usage: values.usage,
      dosage: values.dosage,
      diagnosisTags: values.diagnosisTags,
      status: values.status,
      medicationType: values.medicationType,
      isActive: values.isActive,
      beginDate: formatDate(formatBeginDate),
      endDate: values.endDate,
    };
    if (values.endDate != null && values.endDate !== "") {
      const formatEndDate = new Date(values.endDate);
      medicationParams = {
        ...medicationParams,
        endDate: formatDate(formatEndDate),
      };
    }
    medicationsPutMutation(medicationParams);
    reset();
    navigate("/user/hr/medications");
  };

  if (isFetching) return "Loading...";

  if (data) {
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"Medication and Supplements"}>
            <Flex
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="medicationName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Medication Name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.medicationName?.message}
                    hasError={!!errors.medicationName}
                  />
                )}
              />
              <Controller
                name="medicationDescription"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="Medication Description"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.medicationDescription?.message}
                    hasError={!!errors.medicationDescription}
                  />
                )}
              />
              <Label htmlFor="beginDate">Begin Date</Label>
              <Controller
                name="beginDate"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={field.value ? new Date(field.value) : null}
                      format="yyyy-MM-dd"
                      oneTap
                      style={{ width: "100%" }}
                      size="lg"
                    />
                    {!!errors.beginDate && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.beginDate.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Label htmlFor="endDate">End Date</Label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={field.value ? new Date(field.value) : null}
                      format="yyyy-MM-dd"
                      oneTap
                      style={{ width: "100%" }}
                      size="lg"
                    />
                    {!!errors.endDate && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.endDate.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Controller
                name="usage"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Usage"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.usage?.message}
                    hasError={!!errors.usage}
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="AS_NEEDED">As Needed</option>
                  </SelectField>
                )}
              />
              <Controller
                name="dosage"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Dosage"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.dosage?.message}
                    hasError={!!errors.dosage}
                  />
                )}
              />
              <Controller
                name="medicationType"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Medication Type"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.medicationType?.message}
                    hasError={!!errors.medicationType}
                  >
                    <option value="Medication">Medication</option>
                    <option value="Supplement">Supplement</option>
                  </SelectField>
                )}
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Status"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.status?.message}
                    hasError={!!errors.status}
                  >
                    <option value="Prescribed and taking as directed">
                      Prescribed and taking as directed
                    </option>
                    <option value="Prescribed and not taking">
                      Prescribed and not taking
                    </option>
                    <option value="Taking, but not prescribed">
                      Taking, but not prescribed
                    </option>
                    <option value="Stopped by Provider">
                      Stopped by Provider
                    </option>
                    <option value="Completed">Completed</option>
                  </SelectField>
                )}
              />
              <Controller
                name="diagnosisTags"
                control={control}
                render={({ field }) => {
                  return (
                    <TagsInput
                      placeHolder="Tags"
                      value={field?.value || []}
                      onChange={(tags) => field.onChange(tags)}
                    />
                  );
                }}
              />
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <SwitchField
                    label="Is Active"
                    labelPosition="end"
                    {...field}
                    isChecked={field.value === 1}
                    onChange={(event) =>
                      field.onChange(event.target.checked ? 1 : 0)
                    }
                  />
                )}
              />
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
  }
}
